import {
    SendMessageRequest,
    Session,
    SessionOnConnectionBrokenParams,
    SessionOnConnectionEstablishedParams,
    SessionOnEndedParams,
    SessionOnMessageParams,
    SessionStatus,
    SessionStatusHandler,
    TranscriptRequest,
} from './Session';
import { CognitoCredentialsManager } from '../../utils/CognitoCredentialsManager';
import axios, { isAxiosError } from 'axios';
import * as api from '@amzn/ring-cs-chatbot-service-model';
import { ContentTypeEnumSchema, JsonContentTypeEnumSchema, WebSocketTopic } from '@amzn/ring-cs-chatbot-service-model';
import '@amzn/amazon-connect-websocket-manager';
import { SubscriptionContainer } from '../../utils/SubscriptionContainer';
import { apiPrefix } from '../../settings';
import { ReceiptRateLimiter } from './ReceiptRateLimiter';
import { TypingRateLimiter } from './TypingRateLimiter';
import { ConnectionRateLimiter } from './ConnectionRateLimiter';

const RECEIPT_EVENTS = [
    api.ContentTypeEnumSchema.enum['application/vnd.amazonaws.connect.event.message.delivered'] as string,
    api.ContentTypeEnumSchema.enum['application/vnd.amazonaws.connect.event.message.read'] as string,
];

const TYPING_EVENTS = [
    api.ContentTypeEnumSchema.enum['application/vnd.amazonaws.connect.event.typing'] as string,
];

const WebSocketManager = (global as any).connect.WebSocketManager;
WebSocketManager.setGlobalConfig({
    loggerConfig: {
        useDefaultLogger: true,
        level: 10, // Log Level Debug
    },
});

const ApiEndpoints = {
    START: 'start',
    SEND_MESSAGE: 'sendMessage',
    CONNECTION: 'connection',
    TRANSCRIPT: 'transcript',
    END: 'end',
};

export type ProxySessionStorageProperties = {
    identityId: string;
    token: string;
    sessionId?: string;
    attributes: Record<string, string>;
};

export class ProxySession implements Session {
    private readonly connectionRateLimiter: ConnectionRateLimiter;
    private readonly receiptRateLimiter: ReceiptRateLimiter;
    private readonly typingRateLimiter: TypingRateLimiter;

    private readonly statusContainer: SubscriptionContainer<SessionStatus>;
    private readonly credentialsManager: CognitoCredentialsManager;

    private readonly sessionId?: string;
    private readonly attributes: Record<string, string>;

    private websocketManager?: typeof WebSocketManager;
    private subscriptions: (() => void)[] = [];
    private onConnectionEstablishedHandler?: () => void;
    private onConnectionBrokenHandler?: () => void;
    private onMessageHandler?: (message: api.ResponseMessage) => void;
    private onEndedHandler?: () => void;

    constructor (properties: ProxySessionStorageProperties) {
        this.connectionRateLimiter = new ConnectionRateLimiter();
        this.receiptRateLimiter = new ReceiptRateLimiter();
        this.typingRateLimiter = new TypingRateLimiter();

        this.statusContainer = new SubscriptionContainer<SessionStatus>(SessionStatus.CREATED);

        this.credentialsManager = new CognitoCredentialsManager({
            identityId: properties.identityId,
            token: properties.token,
        });

        this.sessionId = properties.sessionId;
        this.attributes = properties.attributes;
    }

    connect = async () => {
        if (!this.websocketManager) {
            this.websocketManager = WebSocketManager.create();

            this.websocketManager.init(async () => await this.getWebSocketConfiguration());
            console.log('Websocket manager initialized.');
            this.subscriptions = [
                this.websocketManager.onConnectionGain(() => {
                    this.statusContainer.setValue(SessionStatus.ESTABLISHED);
                    this.onConnectionEstablishedHandler?.();
                }),
                this.websocketManager.onConnectionLost(() => {
                    this.statusContainer.setValue(SessionStatus.BROKEN);
                    this.onConnectionBrokenHandler?.();
                }),
                this.websocketManager.onMessage(WebSocketTopic.AWS_SHERLOCK, (message: api.ResponseMessage) => {
                    this.onMessageHandler?.(message);
                    if (message.contentType === ContentTypeEnumSchema.enum['application/json'] && message.content) {
                        try {
                            const jsonContent = JSON.parse(message.content);

                            if (jsonContent?.type === JsonContentTypeEnumSchema.enum['application/chat.event.session.ended']) {
                                console.log('Received session ended event, clearing internal session');
                                this.internalEnd();
                            }
                        } catch (err) {
                            console.warn('Message with content type application/json does not have parseable content');
                        }
                    }
                }),
                this.websocketManager.onInitFailure(() => this.internalEnd()),
            ];

            this.statusContainer.setValue(SessionStatus.CONNECTING);
        }
    };

    getAttributes = (): Record<string, string> => {
        return this.attributes;
    };

    getSessionId = (): string | undefined => {
        return this.sessionId;
    };

    getWebSocketConfiguration = async () => {
        const url = await this.connectionRateLimiter.getWsConnection(this.getWsApi);
        return {
            webSocketTransport: {
                url,
                transportLifeTimeInSeconds: 3600, // 60 mins
            },
        };
    };

    getWsApi = async () => {
        let wsApi: string;
        try {
            const endpoint = `${apiPrefix}/api/${ApiEndpoints.CONNECTION}`;
            const requestData: api.ConnectionRequest = {
                sessionId: this.sessionId,
            };
            const request = await this.credentialsManager.getSignedRequest(endpoint, 'POST', requestData);
            const response = await axios.request<api.ConnectionResponse>(request);
            wsApi = response.data.websocket;
        } catch (e) {
            if (isAxiosError(e) && e.message === 'Network Error') {
                // so that websocketManager recognizes it as a NetworkingError and retries connection
                throw {
                    _debug: {
                        type: 'NetworkingError'
                    }
                };
            }
            throw new Error(`error calling connect: ${e}`);
        }

        if (!wsApi) {
            throw new Error('Invalid connection response');
        }
        return wsApi
    }

    sendMessage = async (params: SendMessageRequest) => {
        if (!this.websocketManager) {
            throw new Error('connect must be called first');
        }

        const apiSend = async () => {
            const endpoint = `${apiPrefix}/api/${ApiEndpoints.SEND_MESSAGE}`;
            const requestData: api.SendMessageRequest = {
                ...params,
                sessionId: this.sessionId,
            };
            const request = await this.credentialsManager.getSignedRequest(endpoint, 'POST', requestData);
            const response = await axios.request<api.SendMessageResponse>(request);
            return response.data;
        }

        if (params.message.type === api.MessageType.EVENT) {
            if (TYPING_EVENTS.includes(params.message.contentType)) {
                this.typingRateLimiter.triggerTyping(async () => {
                    await apiSend();
                });
                return {};
            } else if (RECEIPT_EVENTS.includes(params.message.contentType)) {
                this.receiptRateLimiter.sendReceipt(async () => {
                    await apiSend();
                });
                return {};
            }
        }

        return await apiSend();
    };

    transcript = async (params: TranscriptRequest) => {
        if (!this.websocketManager) {
            throw new Error('connect must be called first');
        }

        try {
            const endpoint = `${apiPrefix}/api/${ApiEndpoints.TRANSCRIPT}`;
            const requestData: api.TranscriptRequest = {
                scanDirection: api.TranscriptScanDirection.BACKWARD,
                maxResults: 15,
                ...params,
                sessionId: this.sessionId,
            };
            const request = await this.credentialsManager.getSignedRequest(endpoint, 'POST', requestData);
            const response = await axios.request<api.TranscriptResponse>(request);
            return response.data;
        } catch (e) {
            throw new Error(`error calling transcript: ${e}`);
        }
    };

    end = async () => {
        try {
            const endpoint = `${apiPrefix}/api/${ApiEndpoints.END}`;
            const requestData: api.EndRequest = {
                sessionId: this.sessionId,
            };
            const request = await this.credentialsManager.getSignedRequest(endpoint, 'POST', requestData);
            await axios.request(request);
        } catch (e) {
            throw new Error(`error calling end: ${e}`);
        } finally {
            this.internalEnd();
        }
    };

    onStatusUpdate = (handler: SessionStatusHandler) => {
        return this.statusContainer.onUpdate(handler);
    };

    private internalEnd = () => {
        const endedHandler = this.onEndedHandler;

        this.clear();

        // mark the session ended
        this.statusContainer.setValue(SessionStatus.ENDED);
        endedHandler?.();
    };

    clear = () => {
        // not really needed, but remove credentials from memory for safety
        this.credentialsManager.removeCredentials();

        // shut everything down
        this.onConnectionEstablishedHandler = undefined;
        this.onConnectionBrokenHandler = undefined;
        this.onMessageHandler = undefined;
        this.onEndedHandler = undefined;

        if (this.websocketManager) {
            console.log('Closing websocket');
            this.websocketManager.closeWebSocket();
        }
        this.subscriptions.forEach(unsubscribe => unsubscribe());
        console.log('Websocket closed. All event subscriptions are cleared.');

        this.receiptRateLimiter.cancelPendingReceiptEvent();
    };

    onConnectionEstablished = (params: SessionOnConnectionEstablishedParams) => {
        this.onConnectionEstablishedHandler = params.handler;
    };

    onConnectionBroken = (params: SessionOnConnectionBrokenParams) => {
        this.onConnectionBrokenHandler = params.handler;
    };

    onMessage = (params: SessionOnMessageParams) => {
        this.onMessageHandler = params.handler;
    };

    onEnded = (params: SessionOnEndedParams) => {
        this.onEndedHandler = params.handler;
    };
}
